import { isPlatformBrowser, isPlatformServer, NgClass, NgFor } from '@angular/common';
import {
	AfterViewInit,
	ChangeDetectionStrategy,
	Component,
	ElementRef,
	EventEmitter,
	HostListener,
	Inject,
	Input,
	OnChanges,
	Output,
	PLATFORM_ID,
	SimpleChanges,
	TrackByFunction,
	ViewChild,
} from '@angular/core';
import { opacity, ServerBrowserTransitionService, TrackImpressionDirective } from '@woolworthsnz/styleguide';
import { RecipeStamp } from '@woolworthsnz/trader-api';
import { first } from 'rxjs';
import { ssrPlaceholders } from '../grid-container/grid-container.component';
import { Stamp, StampType } from '../../models';
import { StampComponent } from '../stamp/stamp.component';

/* eslint-disable @typescript-eslint/naming-convention */
export interface TealeumImpression {
	tealium_event: string;
	interaction_source: string;
	recipe_name: any[];
	recipe_id: any[];
	recipe_category: string[];
	recipe_prep_time: any[];
	recipe_cook_time: any[];
	recipe_servings: any[];
}
/* eslint-enable @typescript-eslint/naming-convention */

@Component({
	selector: 'recipe-grid',
	templateUrl: './grid.component.html',
	styleUrls: ['./grid.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	animations: [...opacity],
	standalone: true,
	imports: [NgClass, NgFor, StampComponent, TrackImpressionDirective]
})
export class GridComponent implements AfterViewInit, OnChanges {
	@ViewChild('recipeList') recipeList: ElementRef<HTMLUListElement>;
	@Input() savedRecipeIds: number[] = [];
	@Input() recipeStamps: Stamp[] = [];
	@Input() mode: 'grid' | 'carousel' = 'grid';
	@Input() stampType: StampType = StampType.recipe;

	// Meal Planning specific
	@Input() initialServings = 1;

	@Output() columnCountChange = new EventEmitter<number>();
	@Output() saveRecipe = new EventEmitter<{ recipeId: number; isSaved: boolean }>();
	@Output() addServing = new EventEmitter<{ stamp: Stamp; quantity: number }>();
	@Output() removeServing = new EventEmitter<{ stamp: Stamp; quantity: number }>();
	@Output() stampClick = new EventEmitter<{ recipe: Stamp; slot: number }>();

	recipeImpressionArgs: TealeumImpression[] = [];

	private columnCount: number;

	constructor(
		@Inject(PLATFORM_ID) private platformId: Object,
		private serverBrowserTransitionService: ServerBrowserTransitionService
	) {
		if (isPlatformBrowser(this.platformId)) {
			this.serverBrowserTransitionService.transitionToBrowser$.pipe(first((isBrowser) => isBrowser)).subscribe(() => {
				if (this.mode === 'grid') {
					this.columnCountChange.emit(this.getColumnCount());
				}
			});
		}
	}

	get recipeStampList(): Stamp[] {
		if (isPlatformServer(this.platformId)) {
			return this.recipeStamps;
		}

		return this.recipeStamps?.map((stamp) => ({
			...stamp,
			recipe: { ...stamp.recipe, saved: !!this.savedRecipeIds?.includes(stamp.recipe.id) },
		}));
	}

	@HostListener('window:resize')
	onResize(): void {
		const newColumnCount = this.getColumnCount();
		if (this.mode !== 'grid' || newColumnCount === this.columnCount) {
			return;
		}
		this.columnCount = newColumnCount;
		this.columnCountChange.emit(this.columnCount);
	}

	trackByFn: TrackByFunction<RecipeStamp> = (_, i) => i.recipeId;

	ngAfterViewInit(): void {
		if (this.mode === 'grid') {
			this.columnCountChange.emit(this.getColumnCount());
		}
		this.recipeImpressionArgs = this.recipeStamps?.map(this.getRecipeImpressionData) || [];
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.recipes) {
			this.recipeImpressionArgs = changes.recipes.currentValue?.map(this.getRecipeImpressionData) || [];
		}
	}

	getRecipeImpressionData(stamp: Stamp): TealeumImpression {
		return {
			tealium_event: 'recipe_list_view',
			interaction_source: 'page',
			recipe_name: [stamp.recipe?.title],
			recipe_id: [stamp.recipe?.id],
			recipe_category: ['Recipes'],
			recipe_prep_time: [stamp.recipe?.prepDuration],
			recipe_cook_time: [stamp.recipe?.cookDuration],
			recipe_servings: [stamp.recipe?.servings],
		};
	}

	private getColumnCount(): number {
		// use an uncomputed value for SSR
		if (isPlatformServer(this.platformId)) {
			return ssrPlaceholders;
		}
		const gridComputedStyle = getComputedStyle(this.recipeList.nativeElement);
		const gridColumnCount = gridComputedStyle.getPropertyValue('grid-template-columns').split(' ').length;

		return gridColumnCount;
	}
}
